package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Card styling definitions
 */
object CardStyles : StyleSheet() {
    val card by style {
        backgroundColor(Color.white)
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, Color("#e0e0e0"))
        property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        position(Position.Relative)
        overflow("hidden")
    }

    val cardContent by style {
        padding(20.px)
    }

    val cardHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        marginBottom(15.px)
    }

    val cardIcon by style {
        width(40.px)
        height(40.px)
        backgroundColor(SXMUI.buttonBackgroundPrimary.value())
        borderRadius(50.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        marginRight(15.px)

        type("span").style {
            fontSize(20.px)
            color(Color.white)
        }
    }

    val cardLabel by style {
        position(Position.Absolute)
        top((-10).px)
        right(10.px)
        padding(2.px, 8.px)
        borderRadius(4.px)
        fontSize(12.px)
        fontWeight("bold")
        property("z-index", "10")
    }

    val cardLabelSuccess by style {
        backgroundColor(Color("#28a745"))
        color(Color.white)
    }

    val cardLabelWarning by style {
        backgroundColor(Color("#ffc107"))
        color(Color("#212529"))
    }

    val cardLabelInfo by style {
        backgroundColor(Color("#17a2b8"))
        color(Color.white)
    }

    val cardLabelDanger by style {
        backgroundColor(Color("#dc3545"))
        color(Color.white)
    }

    val cardHighlighted by style {
        backgroundColor(Color("#f0f7ff"))
        border(1.px, LineStyle.Solid, SXMUI.buttonBackgroundPrimary.value())
    }

    val cardMetric by style {
        textAlign("center")

        type("h2").style {
            margin(5.px, 0.px)
            fontSize(32.px)
            color(SXMUI.buttonBackgroundPrimary.value())
        }

        type("small").style {
            color(Color("#666"))
            fontSize(12.px)
            property("text-transform", "uppercase")
            letterSpacing(1.px)
        }
    }

    val cardActions by style {
        paddingTop(15.px)
        borderTop(1.px, LineStyle.Solid, Color("#e0e0e0"))
        marginTop(15.px)
        display(DisplayStyle.Flex)
        gap(10.px)
    }
}

/**
 * Card label types for styling
 */
enum class CardLabelType {
    SUCCESS, WARNING, INFO, DANGER
}

/**
 * Configuration for card component
 */
class CardConfig {
    var highlighted: Boolean = false
    var label: String? = null
    var labelType: CardLabelType = CardLabelType.SUCCESS
    var icon: String? = null
    var onClick: (() -> Unit)? = null
    var className: String? = null
}

/**
 * Standard card component with optional label, icon, and various styling options
 *
 * @param title Optional card title
 * @param config Configuration for card appearance and behavior
 * @param content Card content
 */
@Composable
fun Card(
    title: String? = null,
    config: CardConfig.() -> Unit = {},
    content: @Composable () -> Unit
) {
    val cardConfig = CardConfig().apply(config)

    Div(attrs = {
        classes(CardStyles.card)
        if (cardConfig.highlighted) {
            classes(CardStyles.cardHighlighted)
        }
        cardConfig.className?.let { classes(it) }
        cardConfig.onClick?.let { onClick ->
            style { cursor("pointer") }
            onClick { onClick() }
        }
    }) {
        // Label badge (if specified)
        cardConfig.label?.let { labelText ->
            Span(attrs = {
                classes(CardStyles.cardLabel)
                when (cardConfig.labelType) {
                    CardLabelType.SUCCESS -> classes(CardStyles.cardLabelSuccess)
                    CardLabelType.WARNING -> classes(CardStyles.cardLabelWarning)
                    CardLabelType.INFO -> classes(CardStyles.cardLabelInfo)
                    CardLabelType.DANGER -> classes(CardStyles.cardLabelDanger)
                }
            }) {
                Text(labelText)
            }
        }

        Div(attrs = { classes(CardStyles.cardContent) }) {
            // Header with optional icon and title
            if (cardConfig.icon != null || title != null) {
                Div(attrs = { classes(CardStyles.cardHeader) }) {
                    cardConfig.icon?.let { iconName ->
                        Div(attrs = { classes(CardStyles.cardIcon) }) {
                            Span(attrs = { classes("material-icons") }) {
                                Text(iconName)
                            }
                        }
                    }
                    title?.let { titleText ->
                        H4(attrs = { style { margin(0.px) } }) {
                            Text(titleText)
                        }
                    }
                }
            }

            // Main content
            content()
        }
    }
}

/**
 * Simple card with just content - most basic usage
 */
@Composable
fun SimpleCard(content: @Composable () -> Unit) {
    Card(content = content)
}

/**
 * Metric card for displaying statistics with icon and title
 */
@Composable
fun MetricCard(
    title: String,
    value: String,
    subtitle: String? = null,
    icon: String,
    config: CardConfig.() -> Unit = {}
) {
    Card(config = config) {
        Row {
            Div(attrs = { style { flex(1) } }) {
                Small { Text(title.uppercase()) }
                H2(attrs = { style { margin(5.px, 0.px) } }) {
                    Text(value)
                }
                subtitle?.let { sub ->
                    Span(attrs = {
                        style {
                            color(Color("#28a745"))
                            fontSize(14.px)
                        }
                    }) {
                        Text(sub)
                    }
                }
            }
            Div(attrs = {
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color("#f0f7ff"))
                    borderRadius(50.percent)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                Span(attrs = {
                    classes("material-icons")
                    style {
                        fontSize(30.px)
                        color(SXMUI.buttonBackgroundPrimary.value())
                    }
                }) { Text(icon) }
            }
        }
    }
}

/**
 * Action card with built-in action buttons
 */
@Composable
fun ActionCard(
    title: String? = null,
    config: CardConfig.() -> Unit = {},
    actions: @Composable () -> Unit = {},
    content: @Composable () -> Unit
) {
    Card(title = title, config = config) {
        content()
        Div(attrs = { classes(CardStyles.cardActions) }) {
            actions()
        }
    }
}